import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import { Alert, Button } from "@boozallen-uip/uswds-react-core";
import { EvaluationContext } from "../stores/EvaluationContext";
import {
  EditorNavigationContext,
  PRODUCT_FIELD_VALIDATION_MESSAGES,
  evaluationHasContentToSave,
} from "../stores/EditorNavigationContext";
import { CatalogContext } from "../stores/CatalogContext";
import useCatalogApi from "../../../hooks/useCatalogApi.hook";
import initializeChaptersForCatalog from "../utils/updateEvaluationForCatalog";
import formatCatalogLabel from "../utils/formatCatalogLabel";
import useOpenReportInEditor from "../hooks/useOpenReportInEditor";
import formatRelativeTimeFromNow from "../utils/formatRelativeTime";
import reportFilename from "../utils/reportFilename";
import honourFragmentIdLinks from "../../../utils/honourFragmentIdLinks";
import {
  ACR_EDITOR_CHAPTER_URL,
  ACR_EDITOR_OVERVIEW_URL,
  ENTITY_TYPE_VENDOR,
  PRODUCT_DETAIL_URL,
  PRODUCT_UI_STATE_ADD_NEW,
  PRODUCT_UI_STATE_SEARCH,
  PRODUCT_UI_STATE_SELECTED_EXISTING,
  PRODUCT_UI_STATE_UNKNOWN,
  PRODUCT_VISIBILITY_BUYERS_ONLY,
  PRODUCT_VISIBILITY_PUBLIC,
  REACT_APP_ACR_API_BASE_URL,
  REACT_APP_ACR_API_PROTOCOL,
  stateLabelValues,
  UNKNOWN_PRODUCT_WARNING,
} from "../../../utlities/core.constants";
import { UserContext } from "../../../pages/UserContext";
import TypeaheadCombobox from "../../TypeaheadCombobox.component";
import useCurrentOrg from "../../../hooks/useCurrentOrg.hook";
import useEditorAccess from "../../../hooks/useEditorAccess.hook";
import useProduct from "../../../hooks/useProduct.hook";
import useReport from "../../../hooks/useReport.hook";
import {
  Header,
  HelpText,
  Pager,
  Related,
  ChapterHelpText,
  ExpandCollapseAll,
} from "../components";
import EditorPagerLink from "../components/EditorPagerLink";
import LicenseSelect from "../components/LicenseSelect";
import { anchorSvg } from "../components/HeaderWithAnchor";
import LICENSE_OPTIONS from "../utils/licenseOptions";
import { getAboutFieldFormatError, normalizeDateToIso } from "../utils/reportValidation";
import helpText from "../data/helpText";
import ProductInformationSection from "../../../pages/Product/components/ProductInformationSection";
import ProductVisibilitySection from "../../../pages/Product/components/ProductVisibilitySection";
import ProductPocSection from "../../../pages/Product/components/ProductPocSection";
import { runSaveDraftProductFlow } from "../../../pages/Product/utils/saveDraftProductFlow";
import { runSubmitProductFlow } from "../../../pages/Product/utils/submitProductFlow";
import {
  createEmptyProductPoc,
  normalizeProductPocPayload,
  validateProductPoc,
} from "../../../pages/Product/utils/productFormValidation";
import "../components/Criteria.css";
import "./About.page.css";

function FormFieldRequired() {
  return <span className="text-red">(Required)</span>;
}

function renderInlineFieldError(message) {
  if (!message) {
    return null;
  }
  return (
    <div className="mt-1 text-sm text-red" role="alert">
      {message}
    </div>
  );
}

function buildProductSavePayload(product) {
  const fedRampStatus = String(product?.fedRampStatus || "").trim();
  return {
    ...product,
    productPoc: normalizeProductPocPayload(product?.productPoc),
    fedRampStatus: fedRampStatus || null,
    hiddenAgencyIds:
      product?.visibility === PRODUCT_VISIBILITY_BUYERS_ONLY
        ? [...new Set((product?.hiddenAgencyIds || []).map((id) => String(id)).filter(Boolean))]
        : [],
  };
}

const PRODUCT_FIELD_FOCUS_IDS = {
  "product.name": "evaluation-product-name",
  "product.category": "evaluation-product-category",
  "product.description": "evaluation-product-description",
  "product.visibility": "evaluation-product-visibility",
  "product.version": "evaluation-product-version",
  "productPoc.phoneNumber": "productPocPhone",
  "productPoc.email": "productPocEmail",
  "productPoc.zipCode": "productPocZipCode",
};

const PRODUCT_FIELD_FOCUS_ORDER = [
  "product.name",
  "product.category",
  "product.description",
  "product.visibility",
  "product.version",
  "productPoc.phoneNumber",
  "productPoc.email",
  "productPoc.zipCode",
];
function Field({ id, label, children, helpType, helpField, error }) {
  const hasError = Boolean(error && typeof error === "string");
  const control = hasError && React.isValidElement(children)
    ? React.cloneElement(children, {
      "aria-invalid": true,
      "aria-describedby": `${id}-error`,
    })
    : children;
  return (
    <div className={`field mb-4${hasError ? " field--error" : ""}`}>
      <label id={`${id}-label`} htmlFor={id} className="usa-label">
        {label}
      </label>
      {control}
      {helpType && helpField && <HelpText type={helpType} field={helpField} />}
      {hasError && <div id={`${id}-error`} className="field-error">{error}</div>}
    </div>
  );
}

Field.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  helpType: PropTypes.string,
  helpField: PropTypes.string,
  error: PropTypes.string,
};

Field.defaultProps = {
  helpType: null,
  helpField: null,
  error: null,
};

export default function About() {
  const unsavedChangesPrompt =
    "This will discard your in-progress product. Are you sure you want to continue?";
  const location = useLocation();
  const { evaluation, setEvaluation, pendingRestore, restoreDraft, discardDraft } =
    useContext(EvaluationContext);
  const editorNavigation = useContext(EditorNavigationContext);
  const finishProductNudge = editorNavigation?.finishProductNudge ?? 0;
  const { getCatalog } = useContext(CatalogContext);
  const { getCatalogsList } = useCatalogApi();
  const { openReportInEditor, ensureEditorCatalog } = useOpenReportInEditor();
  const { userEntity, getEntityPrimaryPocAddress } = useContext(UserContext);
  const {
    fetchFilteredProductAll,
    fetchCategories,
    fetchFedRampStatuses,
    pscCodes,
    submitProduct,
    saveDraftProduct,
    updateProduct,
  } = useProduct();
  const { fetchReportsByProduct } = useReport();

  // Owning org (Vendor or Agency). Products/reports can be owned by either; `vendorId` is kept as the
  // generic "my org id" used for org-scoped search (myListings), while `ownerPayload` carries the
  // correct id field (vendorId vs. agencyId) into create/update product calls.
  const currentOrg = useCurrentOrg();
  const vendorId = currentOrg?.id ?? null;
  // Anonymous / unauthorized users work in Public Mode: they have no org to search, so they type any
  // product name and it is committed as a download-only "unknown" product (no autosave/submit).
  const isPublicMode = !useEditorAccess();
  // Only carry an owner payload when the user actually has an owning org; otherwise leave it null so
  // create/draft calls fall back to the legacy `vendorId` arg.
  const ownerPayload = currentOrg?.hasOrg ? currentOrg.ownerPayload : null;

  const [catalogList, setCatalogList] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState(null);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [selectedCatalogId, setSelectedCatalogId] = useState(() => evaluation?.catalog ?? null);
  const [productSuggestions, setProductSuggestions] = useState([]);
  const [productSearchInput, setProductSearchInput] = useState("");
  const [productSearchLoading, setProductSearchLoading] = useState(false);
  const [productDropdownOpen, setProductDropdownOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [fedRampStatuses, setFedRampStatuses] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [agencyLoadError, setAgencyLoadError] = useState(null);
  const [hiddenAgencySelectionAnnouncement, setHiddenAgencySelectionAnnouncement] = useState("");
  const [productNameDuplicateError, setProductNameDuplicateError] = useState(undefined);
  const [productPocFieldErrors, setProductPocFieldErrors] = useState({});
  const [productSuccessBannerMessage, setProductSuccessBannerMessage] = useState(null);
  const [isProductActionInFlight, setIsProductActionInFlight] = useState(false);
  const [showFinishProductReminder, setShowFinishProductReminder] = useState(false);
  const productSearchDebounceRef = useRef(null);
  const isProductSearchInputFocusedRef = useRef(false);
  const evaluationRef = useRef(evaluation);
  const productActionLockRef = useRef(false);

  useEffect(() => {
    evaluationRef.current = evaluation;
  }, [evaluation]);

  // The author tried to leave with an unsaved new product open: bring the Product section into view,
  // move focus to the product name, and surface the finish-it reminder rendered by the add-new form.
  useEffect(() => {
    if (finishProductNudge <= 0) return;
    setShowFinishProductReminder(true);
    const section = document.getElementById("product-editor");
    if (section && typeof section.scrollIntoView === "function") {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    const nameField = document.getElementById("evaluation-product-name");
    if (nameField && typeof nameField.focus === "function") {
      nameField.focus({ preventScroll: true });
    }
  }, [finishProductNudge]);

  const productUiState = evaluation?.product_ui_state ?? PRODUCT_UI_STATE_SEARCH;
  const showProductValidationErrors = Boolean(evaluation?.show_product_validation_errors);
  const productSubmitError = evaluation?.product_submit_error;
  const product = evaluation?.product ?? {};
  const isAddNewProductMode = productUiState === PRODUCT_UI_STATE_ADD_NEW;
  const isSearchProductMode = productUiState === PRODUCT_UI_STATE_SEARCH;
  const isSelectedExistingProductMode = productUiState === PRODUCT_UI_STATE_SELECTED_EXISTING;
  const isUnknownProductMode = productUiState === PRODUCT_UI_STATE_UNKNOWN;
  // Only buyers (agency users) can build download-only ACRs for products outside their org.
  const canUseUnknownProduct = Boolean(currentOrg?.isAgency);
  const selectedProductDetailsUrl = product?.id
    ? PRODUCT_DETAIL_URL.replace(":productId", String(product.id))
    : null;
  const previousProductUiStateRef = useRef(productUiState);
  const closeProductSuccessBanner = useCallback(() => {
    setProductSuccessBannerMessage(null);
  }, []);

  const productFieldError = useCallback(
    (key) =>
      showProductValidationErrors && !(product[key] ?? "").trim()
        ? PRODUCT_FIELD_VALIDATION_MESSAGES[key]
        : undefined,
    [showProductValidationErrors, product]
  );
  const clearFieldErrors = useCallback((fields = []) => {
    if (fields.includes("product.name")) {
      setProductNameDuplicateError(undefined);
    }
    const pocFields = fields.filter((field) => field.startsWith("productPoc."));
    if (pocFields.length) {
      setProductPocFieldErrors((prev) => {
        let changed = false;
        const next = { ...prev };
        pocFields.forEach((field) => {
          if (next[field]) {
            delete next[field];
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }
  }, []);
  const getFieldError = useCallback((fieldPath) => {
    if (fieldPath.startsWith("productPoc.")) {
      return productPocFieldErrors[fieldPath];
    }
    const map = {
      "product.name": "name",
      "product.version": "version",
      "product.description": "description",
      "product.category": "category",
      "product.visibility": "visibility",
    };
    const key = map[fieldPath];
    if (fieldPath === "product.visibility") {
      return showProductValidationErrors && !product?.visibility
        ? "Select a product visibility option."
        : undefined;
    }
    if (fieldPath === "product.name" && isAddNewProductMode && productNameDuplicateError) {
      return productNameDuplicateError;
    }
    return key ? productFieldError(key) : undefined;
  }, [productFieldError, productNameDuplicateError, isAddNewProductMode, productPocFieldErrors, showProductValidationErrors, product?.visibility]);
  const focusFieldForError = useCallback((field) => {
    const targetId = PRODUCT_FIELD_FOCUS_IDS[field];
    if (!targetId) return;
    const element = document.getElementById(targetId);
    if (element && typeof element.focus === "function") {
      element.focus();
    }
  }, []);
  const focusFirstProductErrorField = useCallback((errorMap) => {
    const firstErrorField = PRODUCT_FIELD_FOCUS_ORDER.find((field) => Boolean(errorMap?.[field]));
    if (firstErrorField) {
      focusFieldForError(firstErrorField);
    }
  }, [focusFieldForError]);
  const setProduct = useCallback((updater) => {
    setEvaluation((prev) => {
      const prevProduct = prev.product ?? {};
      const nextProduct = typeof updater === "function" ? updater(prevProduct) : updater;
      return { ...prev, product: nextProduct };
    });
  }, [setEvaluation]);
  const setProductPoc = useCallback((nextProductPoc) => {
    setProduct((prev) => ({ ...prev, productPoc: nextProductPoc }));
  }, [setProduct]);
  const validateDuplicateProductName = useCallback(async (nameToCheck) => {
    const rawName = String(nameToCheck || "").trim();
    if (!rawName || !vendorId) {
      return;
    }
    try {
      // vendorId here is always the logged-in user's own vendor, so include private/draft
      // listings via myListings=true - the org should see everything it owns.
      const result = await fetchFilteredProductAll(rawName, "", "", vendorId, true);
      const list = Array.isArray(result) ? result : result?.content ?? [];
      const normalizedName = rawName.toLowerCase();
      const duplicate = list.some((candidate) => (
        String(candidate?.name || "").trim().toLowerCase() === normalizedName
      ));
      setProductNameDuplicateError(
        duplicate ? "A product with this name already exists within your organization." : undefined
      );
    } catch {
      // Ignore search errors during blur validation.
    }
  }, [vendorId, fetchFilteredProductAll]);
  const handleProductNameBlur = useCallback(() => {
    if (!isAddNewProductMode) {
      return;
    }
    validateDuplicateProductName(product?.name);
  }, [isAddNewProductMode, product?.name, validateDuplicateProductName]);

  useEffect(() => {
    const previousMode = previousProductUiStateRef.current;
    if (previousMode !== productUiState && productUiState === PRODUCT_UI_STATE_ADD_NEW) {
      validateDuplicateProductName(product?.name);
    }
    previousProductUiStateRef.current = productUiState;
  }, [productUiState, product?.name, validateDuplicateProductName]);
  const normalizedProductVisibility = String(product.visibility || "").trim() || PRODUCT_VISIBILITY_PUBLIC;
  const agencyExclusionsApply = normalizedProductVisibility === PRODUCT_VISIBILITY_BUYERS_ONLY;
  const selectedHiddenAgencies = useMemo(() => {
    const hiddenIds = product.hiddenAgencyIds || [];
    if (!hiddenIds.length) return [];
    const agenciesById = new Map(agencies.map((agency) => [String(agency.id), agency.name]));
    return hiddenIds.map((id) => ({ id: String(id), name: agenciesById.get(String(id)) || null }));
  }, [agencies, product.hiddenAgencyIds]);

  const currentCatalogId = evaluation?.catalog ?? null;
  const catalog = getCatalog(currentCatalogId);
  const versionPrefix = reportFilename(evaluation, false);

  useEffect(() => {
    honourFragmentIdLinks(location);
  }, [location]);

  useEffect(() => {
    setSelectedCatalogId((prev) =>
      currentCatalogId !== undefined && currentCatalogId !== null ? currentCatalogId : prev
    );
  }, [currentCatalogId]);

  const loadCatalogList = useCallback(async () => {
    setLoadingList(true);
    setListError(null);
    const list = await getCatalogsList(20, 1);
    setCatalogList(Array.isArray(list) ? list : []);
    setLoadingList(false);
    if (Array.isArray(list) && list.length === 0) setListError("No catalogs returned.");
  }, [getCatalogsList]);

  useEffect(() => {
    loadCatalogList();
  }, [loadCatalogList]);

  useEffect(() => {
    (async () => {
      try {
        const categoryResult = await fetchCategories();
        const categoryList = Array.isArray(categoryResult) ? categoryResult : categoryResult?.data;
        setCategories(Array.isArray(categoryList) ? categoryList : []);
      } catch {
        setCategories([]);
      }
      try {
        const statuses = await fetchFedRampStatuses();
        setFedRampStatuses(Array.isArray(statuses) ? statuses : []);
      } catch {
        setFedRampStatuses([]);
      }
    })();
  }, [fetchCategories, fetchFedRampStatuses]);

  useEffect(() => {
    if (!vendorId || !isAddNewProductMode) return;
    (async () => {
      try {
        setAgencyLoadError(null);
        const agencyResponse = await axios.get(`${REACT_APP_ACR_API_PROTOCOL}://${REACT_APP_ACR_API_BASE_URL}/api/agency/all`);
        const agencyData = agencyResponse?.data;
        const agencyList = Array.isArray(agencyData) ? agencyData : agencyData?.data;
        setAgencies((Array.isArray(agencyList) ? agencyList : []).filter((agency) => agency?.id && agency?.name));
      } catch {
        setAgencies([]);
        setAgencyLoadError("Unable to load agency options right now.");
      }
    })();
  }, [vendorId, isAddNewProductMode]);

  useEffect(() => {
    const n = selectedHiddenAgencies.length;
    if (n === 0) {
      setHiddenAgencySelectionAnnouncement("No agencies selected to hide this listing from.");
    } else {
      setHiddenAgencySelectionAnnouncement(`${n} agenc${n === 1 ? "y" : "ies"} selected to hide this listing from.`);
    }
  }, [selectedHiddenAgencies.length]);

  // Prefill Vendor section from API when current user has a vendor
  useEffect(() => {
    if (userEntity?.type !== ENTITY_TYPE_VENDOR || !userEntity?.entity) {
      return;
    }
    const { entity } = userEntity;
    const primaryPoc = userEntity.primaryPoc ?? {};
    setEvaluation((prev) => {
      return {
        ...prev,
        vendor: {
          name: primaryPoc.name ?? "",
          company_name: entity.name ?? "",
          address: getEntityPrimaryPocAddress() ?? "",
          email: primaryPoc.email ?? "",
          phone: primaryPoc.phoneNumber ?? "",
          website: "",
        },
      };
    });
  }, [userEntity, setEvaluation]);

  // Debounced product name search (org-scoped) - runs for any user with an owning org.
  const productNameQuery = productSearchInput.trim();
  // Buyers may commit a typed name that is not in their org as a download-only "unknown" product.
  const showUnknownProductOption = canUseUnknownProduct && Boolean(productNameQuery);
  useEffect(() => {
    if (!vendorId || !isSearchProductMode) {
      if (!vendorId) setProductSuggestions([]);
      return () => {};
    }
    if (!productNameQuery) {
      setProductSuggestions([]);
      setProductDropdownOpen(false);
      return () => {};
    }
    if (productSearchDebounceRef.current) {
      clearTimeout(productSearchDebounceRef.current);
    }
    productSearchDebounceRef.current = setTimeout(async () => {
      productSearchDebounceRef.current = null;
      setProductSearchLoading(true);
      if (isProductSearchInputFocusedRef.current) {
        setProductDropdownOpen(true);
      }
      try {
        // vendorId here is always the logged-in user's own vendor, so include private/draft
        // listings via myListings=true - the org should see everything it owns.
        const result = await fetchFilteredProductAll(
          productNameQuery,
          "",
          "",
          vendorId,
          true
        );
        const list = Array.isArray(result) ? result : result?.content ?? [];
        setProductSuggestions(list);
        if (isProductSearchInputFocusedRef.current) {
          setProductDropdownOpen(true);
        }
      } catch {
        setProductSuggestions([]);
        setProductDropdownOpen(false);
      } finally {
        setProductSearchLoading(false);
      }
    }, 300);
    return () => {
      if (productSearchDebounceRef.current) {
        clearTimeout(productSearchDebounceRef.current);
      }
    };
  }, [vendorId, isSearchProductMode, productNameQuery, fetchFilteredProductAll]);

  const loadAndSetCatalog = useCallback(
    async (catalogId) => {
      if (!catalogId) return;
      const catalogItem = catalogList.find((c) => (c.id ?? c.catalog) === catalogId);
      if (!catalogItem) return;
      setListError(null);
      const needsFetch = !getCatalog(catalogId)?.chapters?.length;
      if (needsFetch) setLoadingCatalog(true);
      try {
        const { ok, catalog: editorCatalog, reason } = await ensureEditorCatalog(catalogId);
        if (!ok) {
          setListError(
            reason === "empty-catalog"
              ? "Failed to load catalog or it has no standards."
              : "Failed to load catalog."
          );
          return;
        }
        const updated = initializeChaptersForCatalog(editorCatalog, {
          ...evaluation,
          catalog: catalogId,
        });
        setEvaluation(updated);
      } catch {
        setListError("Failed to load catalog.");
      } finally {
        if (needsFetch) setLoadingCatalog(false);
      }
    },
    [
      catalogList,
      evaluation,
      ensureEditorCatalog,
      getCatalog,
      setEvaluation,
    ]
  );

  const clearEvaluationProductFields = useCallback((prev) => {
    return {
      ...prev,
      report_id: null,
      report_save_status: "idle",
      report_save_error: undefined,
      product_is_unknown: false,
      product: {
        ...prev.product,
        id: undefined,
        name: "",
        version: "",
        description: "",
        category: "",
        pscCode: "",
        fedRampStatus: "",
        visibility: PRODUCT_VISIBILITY_PUBLIC,
        hiddenAgencyIds: [],
        productPoc: createEmptyProductPoc(),
      },
      product_ui_state: PRODUCT_UI_STATE_SEARCH,
    };
  }, []);

  /**
   * Apply search suggestion to evaluation.product. If the product id changed, clear report_id so we
   * do not PATCH an existing report that belongs to a different product (would only change names on the wrong report).
   */
  const mergeSuggestionIntoEvaluationProduct = useCallback((prev, selectedProduct) => {
    const newId = selectedProduct?.id;
    const prevId = prev.product?.id;
    const hasStableProductId = newId != null && newId !== "";
    const productIdentityChanged =
      !hasStableProductId ||
      prevId == null ||
      String(prevId) !== String(newId);

    // When the chosen product is a *different* product (no existing report was opened here),
    // any previously entered ACR content belongs to the prior product and must not carry over
    // (GSAACR-356: "still seeing populated data from the product selected previously"). Reset the
    // report-level content to a clean slate; product fields are set fresh below.
    const clearedReportContent = productIdentityChanged
      ? {
          chapters: {},
          notes: "",
          evaluation_methods_used: "",
          legal_disclaimer: "",
          repository: "",
          feedback: "",
          related_acrs: [],
          about_field_format_errors: {},
          report_save_status: "idle",
        }
      : {};

    return {
      ...prev,
      ...clearedReportContent,
      product_ui_state: hasStableProductId
        ? PRODUCT_UI_STATE_SELECTED_EXISTING
        : PRODUCT_UI_STATE_SEARCH,
      product_is_unknown: false,
      report_id: productIdentityChanged ? null : prev.report_id,
      report_save_error: productIdentityChanged ? undefined : prev.report_save_error,
      product: {
        ...prev.product,
        id: hasStableProductId ? newId : undefined,
        name: selectedProduct.name ?? "",
        version: selectedProduct.version ?? "",
        description: selectedProduct.description ?? "",
        category: selectedProduct.category ?? "",
        pscCode: selectedProduct.pscCode ?? "",
        fedRampStatus: selectedProduct.fedRampStatus ?? "",
        isPublished: selectedProduct.isPublished,
        visibility: selectedProduct.visibility ?? PRODUCT_VISIBILITY_PUBLIC,
        hiddenAgencyIds: Array.isArray(selectedProduct.hiddenAgencyIds) ? selectedProduct.hiddenAgencyIds.map(String) : [],
        productPoc: selectedProduct.productPoc ?? createEmptyProductPoc(),
      },
    };
  }, []);

  const handleCatalogRadioChange = useCallback(
    (id) => {
      setSelectedCatalogId(id);
      if (currentCatalogId == null) {
        loadAndSetCatalog(id);
      }
    },
    [currentCatalogId, loadAndSetCatalog]
  );

  const handleSwitchCatalog = useCallback(() => {
    const message =
      "Switching catalogs may remove entered data and notes from your ACR that are not part of the newly selected catalog.\n\nPlease download your report before switching catalogs to avoid losing information. Select Cancel to save before switching.";
    // eslint-disable-next-line no-alert -- intentional user confirmation before destructive action
    if (!window.confirm(message)) return;
    loadAndSetCatalog(selectedCatalogId);
  }, [loadAndSetCatalog, selectedCatalogId]);

  const handleResetCatalog = useCallback(() => {
    setSelectedCatalogId(currentCatalogId);
  }, [currentCatalogId]);

  const updateField = useCallback(
    (path, value) => {
      setEvaluation((prev) => {
        const parts = path.split(".");
        const next = parts.length === 1
          ? { ...prev, [parts[0]]: value }
          : (() => {
              const [head, ...tail] = parts;
              const nextInner = { ...prev };
              nextInner[head] = (function setNested(obj, keys, val) {
                const o = { ...(obj || {}) };
                if (keys.length === 1) {
                  o[keys[0]] = val;
                  return o;
                }
                o[keys[0]] = setNested(o[keys[0]], keys.slice(1), val);
                return o;
              })(prev[head], tail, value);
              return nextInner;
            })();
        return next;
      });
    },
    [setEvaluation]
  );

  const handleProductSuggestionSelect = useCallback(
    async (selectedProduct) => {
      closeProductSuccessBanner();
      const closeProductDropdown = () => {
        setProductSuggestions([]);
        setProductDropdownOpen(false);
      };

      const productId = selectedProduct?.id;
      const evaluationBefore = evaluationRef.current;

      if (productId == null || productId === "") {
        setEvaluation((prev) => mergeSuggestionIntoEvaluationProduct(prev, selectedProduct));
        closeProductDropdown();
        return;
      }

      let reports;
      try {
        reports = await fetchReportsByProduct(productId);
      } catch {
        // eslint-disable-next-line no-alert -- intentional user feedback when API fails
        alert("We could not check for an existing ACR for this product. Please try again.");
        setEvaluation((prev) => clearEvaluationProductFields(prev));
        closeProductDropdown();
        return;
      }

      // Always uses reports[0] because there can only be 1 report per report/product combination
      const existingReport = Array.isArray(reports) && reports.length > 0 ? reports[0] : null;

      if (!existingReport) {
        setEvaluation((prev) => mergeSuggestionIntoEvaluationProduct(prev, selectedProduct));
        closeProductDropdown();
        return;
      }

      const displayName =
        (selectedProduct.name ?? existingReport.productName ?? "").trim() || "this product";
      // eslint-disable-next-line no-alert -- intentional confirm before opening existing report
      const openExisting = window.confirm(
        `An ACR already exists for ${displayName}. Would you like to open it?`
      );

      if (!openExisting) {
        setEvaluation((prev) => clearEvaluationProductFields(prev));
        closeProductDropdown();
        return;
      }

      if (evaluationHasContentToSave(evaluationBefore)) {
        // eslint-disable-next-line no-alert -- intentional confirm before discarding in-progress ACR
        const discardOk = window.confirm(
          "Your current ACR will be replaced with the existing report. Unsaved changes will be lost.\n\nContinue?"
        );
        if (!discardOk) {
          closeProductDropdown();
          return;
        }
      }

      const { catalogId } = existingReport;
      setListError(null);
      const needsCatalogFetch = !getCatalog(catalogId)?.chapters?.length;
      if (needsCatalogFetch) setLoadingCatalog(true);
      let openResult;
      try {
        // Shared "open existing report" flow (loads catalog/product/vendor + builds evaluation).
        openResult = await openReportInEditor({
          report: existingReport,
          previousEvaluation: evaluationBefore,
        });
      } finally {
        if (needsCatalogFetch) setLoadingCatalog(false);
      }

      if (!openResult.ok) {
        const message =
          {
            "product-load-failed": "We could not load product details. Please try again.",
            "vendor-load-failed": "We could not load vendor details. Please try again.",
          }[openResult.reason] ||
          "We couldn't load the catalog for this report. Please try again. If this continues, contact support.";
        // eslint-disable-next-line no-alert -- intentional user feedback when opening a report fails
        alert(message);
        setEvaluation((prev) => clearEvaluationProductFields(prev));
        closeProductDropdown();
        return;
      }

      setSelectedCatalogId(openResult.catalogId ?? catalogId);
      closeProductDropdown();
    },
    [
      fetchReportsByProduct,
      getCatalog,
      openReportInEditor,
      mergeSuggestionIntoEvaluationProduct,
      clearEvaluationProductFields,
      setEvaluation,
    ]
  );

  const handleAddNewProduct = useCallback(() => {
    closeProductSuccessBanner();
    setProductPocFieldErrors({});
    const searchText = productSearchInput.trim();
    setEvaluation((prev) => ({
      ...prev,
      product_ui_state: PRODUCT_UI_STATE_ADD_NEW,
      product_is_unknown: false,
      report_id: null,
      report_save_error: undefined,
      product: {
        ...prev.product,
        id: undefined,
        name: searchText,
        version: "",
        description: "",
        category: "",
        pscCode: "",
        fedRampStatus: "",
        visibility: PRODUCT_VISIBILITY_PUBLIC,
        hiddenAgencyIds: [],
        productPoc: createEmptyProductPoc(),
      },
    }));
    setProductDropdownOpen(false);
    setProductSuggestions([]);
    setProductSearchInput("");
  }, [setEvaluation, productSearchInput]);

  /**
   * Commit a typed product name that is not in the author's organization ("unknown product").
   * The report can be built and downloaded, but autosave/submit stay disabled (see Report page and
   * EditorNavigationContext autosave skip, which both key off product_is_unknown / missing product id).
   */
  const handleUseUnknownProduct = useCallback(() => {
    closeProductSuccessBanner();
    const typedName = productSearchInput.trim();
    if (!typedName) return;
    setProductPocFieldErrors({});
    setEvaluation((prev) => ({
      ...prev,
      product_ui_state: PRODUCT_UI_STATE_UNKNOWN,
      product_is_unknown: true,
      report_id: null,
      report_save_error: undefined,
      show_product_validation_errors: false,
      product_submit_error: undefined,
      product: {
        ...prev.product,
        id: undefined,
        name: typedName,
        version: "",
        description: "",
        category: "",
        pscCode: "",
        fedRampStatus: "",
        visibility: PRODUCT_VISIBILITY_PUBLIC,
        hiddenAgencyIds: [],
        isPublished: undefined,
        productPoc: createEmptyProductPoc(),
      },
    }));
    setProductDropdownOpen(false);
    setProductSuggestions([]);
    setProductSearchInput("");
  }, [setEvaluation, productSearchInput]);

  const handleCancelNewProduct = useCallback(() => {
    // eslint-disable-next-line no-alert -- match ProductSubmit unsaved changes confirmation behavior
    if (!window.confirm(unsavedChangesPrompt)) {
      return;
    }
    closeProductSuccessBanner();
    setProductPocFieldErrors({});
    setEvaluation((prev) => ({
      ...prev,
      product_ui_state: PRODUCT_UI_STATE_SEARCH,
      show_product_validation_errors: false,
      product_submit_error: undefined,
      product: {
        ...prev.product,
        version: "",
        description: "",
        category: "",
        pscCode: "",
        fedRampStatus: "",
        visibility: PRODUCT_VISIBILITY_PUBLIC,
        hiddenAgencyIds: [],
        productPoc: createEmptyProductPoc(),
      },
    }));
    setProductSearchInput("");
  }, [setEvaluation, unsavedChangesPrompt]);

  const handleChooseDifferentProduct = useCallback(() => {
    closeProductSuccessBanner();
    setProductPocFieldErrors({});
    setEvaluation((prev) => ({
      ...prev,
      product_ui_state: PRODUCT_UI_STATE_SEARCH,
      product_is_unknown: false,
      product_submit_error: undefined,
      show_product_validation_errors: false,
      report_id: null,
      product: {
        ...prev.product,
        id: undefined,
        name: "",
        version: "",
        description: "",
        category: "",
        pscCode: "",
        fedRampStatus: "",
        visibility: PRODUCT_VISIBILITY_PUBLIC,
        hiddenAgencyIds: [],
        isPublished: undefined,
        productPoc: createEmptyProductPoc(),
      },
    }));
    setProductSuggestions([]);
    setProductDropdownOpen(false);
    setProductSearchInput("");
  }, [setEvaluation]);

  const handleSaveDraftProduct = useCallback(async () => {
    if (productActionLockRef.current || isProductActionInFlight) {
      return;
    }
    productActionLockRef.current = true;
    setIsProductActionInFlight(true);
    closeProductSuccessBanner();
    try {
      if (!vendorId) {
        return;
      }

      setEvaluation((prev) => ({
        ...prev,
        product_submit_error: undefined,
        show_product_validation_errors: false,
      }));

      const result = await runSaveDraftProductFlow({
        workingProductId: product?.id ?? null,
        product,
        hyperlink: {},
        loadedHyperlink: {},
        vendorId,
        ownerPayload,
        buildProductSavePayload,
        saveDraftProduct,
        updateProduct,
        validateDraftHyperlink: () => null,
        hasAnyHyperlinkInput: () => false,
        buildHyperlinkPayloadForApi: () => ({}),
        hasHyperlinkChanges: () => false,
        updateHyperlink: async () => ({ status: 200 }),
        createHyperlinkForProduct: async () => ({ ok: true }),
        productNameRequiredMessage: PRODUCT_FIELD_VALIDATION_MESSAGES.name,
      });

      if (!result.ok) {
        if (result.type === "validation_errors") {
          const nextErrors = result.errors || {};
          setEvaluation((prev) => ({
            ...prev,
            show_product_validation_errors: true,
            product_submit_error: undefined,
          }));
          setTimeout(() => focusFirstProductErrorField(nextErrors), 0);
          return;
        }
        setEvaluation((prev) => ({
          ...prev,
          product_submit_error: result.message,
        }));
        return;
      }

      setEvaluation((prev) => ({
        ...prev,
        product_ui_state: PRODUCT_UI_STATE_SELECTED_EXISTING,
        show_product_validation_errors: false,
        product_submit_error: undefined,
        product: {
          ...prev.product,
          id: result.resolvedDraftId ?? prev.product?.id,
          isPublished: false,
        },
      }));
      setProductSuccessBannerMessage("Your draft product has been successfully saved.");
    } finally {
      productActionLockRef.current = false;
      setIsProductActionInFlight(false);
    }
  }, [vendorId, ownerPayload, product, saveDraftProduct, updateProduct, setEvaluation, focusFirstProductErrorField, isProductActionInFlight]);

  const handleSubmitNewProduct = useCallback(async () => {
    if (productActionLockRef.current || isProductActionInFlight) {
      return;
    }
    productActionLockRef.current = true;
    setIsProductActionInFlight(true);
    try {
      if (!vendorId) {
        return;
      }

      setEvaluation((prev) => ({
        ...prev,
        product_submit_error: undefined,
        show_product_validation_errors: false,
      }));

    const validateProductOnlySubmit = (productToValidate) => {
      const failures = [];
      const addFailure = (field, message) => failures.push({ field, message });
      if (!String(productToValidate?.name || "").trim()) addFailure("product.name", PRODUCT_FIELD_VALIDATION_MESSAGES.name);
      if (!String(productToValidate?.description || "").trim()) addFailure("product.description", PRODUCT_FIELD_VALIDATION_MESSAGES.description);
      if (!String(productToValidate?.category || "").trim()) addFailure("product.category", PRODUCT_FIELD_VALIDATION_MESSAGES.category);
      if (!productToValidate?.visibility) addFailure("product.visibility", "Select a product visibility option.");
      validateProductPoc(productToValidate?.productPoc).forEach(({ field, message }) => addFailure(field, message));
      return { isValid: failures.length === 0, failures };
    };
    const mapFieldFailures = (failures) => (
      failures.reduce((acc, failure) => {
        if (!acc[failure.field]) {
          acc[failure.field] = failure.message;
        }
        return acc;
      }, {})
    );
    const updatePublishedProductForSubmit = async (targetProductId, productToSave) => {
      const updateProductResponse = await updateProduct(targetProductId, {
        ...productToSave,
        id: targetProductId,
        ...(ownerPayload ?? { vendorId }),
      });
      if (updateProductResponse.status === 403) {
        return { ok: false, reason: "forbidden" };
      }
      if (updateProductResponse.status >= 300) {
        return { ok: false, reason: "product-update-failed" };
      }
      return { ok: true };
    };

      const result = await runSubmitProductFlow({
        workingProductId: product?.id ?? null,
        product,
        hyperlink: {},
        loadedHyperlink: {},
        validateSubmitPayload: validateProductOnlySubmit,
        mapFieldFailures,
        buildProductSavePayload,
        buildHyperlinkPayloadForApi: () => ({}),
        hasHyperlinkChanges: () => false,
        updateHyperlink: async () => ({ status: 200 }),
        createHyperlinkForProduct: async () => ({ ok: true }),
        updatePublishedProduct: updatePublishedProductForSubmit,
        submitProduct,
        vendorId,
        ownerPayload,
      });

      if (!result.ok) {
        if (result.type === "validation_errors") {
          const nextErrors = result.errors || {};
          const pocErrors = Object.keys(nextErrors).reduce((acc, field) => {
            if (field.startsWith("productPoc.")) {
              acc[field] = nextErrors[field];
            }
            return acc;
          }, {});
          setProductPocFieldErrors(pocErrors);
          setEvaluation((prev) => ({
            ...prev,
            show_product_validation_errors: true,
            product_submit_error: undefined,
          }));
          setTimeout(() => focusFirstProductErrorField(nextErrors), 0);
          return;
        }
        setEvaluation((prev) => ({
          ...prev,
          product_submit_error: result.message,
        }));
        return;
      }
      if (result.productUpdateFailed) {
        setEvaluation((prev) => ({
          ...prev,
          product_submit_error: "We couldn't submit your product right now. Please try again. If this continues, contact support.",
        }));
        return;
      }

      setProductPocFieldErrors({});
      setEvaluation((prev) => ({
        ...prev,
        product_ui_state: PRODUCT_UI_STATE_SELECTED_EXISTING,
        show_product_validation_errors: false,
        product_submit_error: undefined,
        product: {
          ...prev.product,
          id: result.resolvedProductId ?? prev.product?.id,
          isPublished: true,
        },
      }));
      setProductSuccessBannerMessage("New product submitted successfully.");
    } finally {
      productActionLockRef.current = false;
      setIsProductActionInFlight(false);
    }
  }, [vendorId, ownerPayload, product, submitProduct, updateProduct, setEvaluation, focusFirstProductErrorField, isProductActionInFlight]);

  const productSearchExtraOptions = useMemo(() => {
    const extras = [
      {
        id: "product-suggestion-add-new",
        label: "End of results – Add a New Product",
        onSelect: handleAddNewProduct,
        className: "product-name-suggestion-add-new",
      },
    ];
    if (showUnknownProductOption) {
      extras.push({
        id: "product-suggestion-unknown",
        label: `Use "${productNameQuery}" (not in your organization) – download only`,
        onSelect: handleUseUnknownProduct,
        className: "product-name-suggestion-add-new",
      });
    }
    return extras;
  }, [handleAddNewProduct, handleUseUnknownProduct, productNameQuery, showUnknownProductOption]);

  const handleRelatedAdd = useCallback(() => {
    setEvaluation((prev) => ({
      ...prev,
      related_acrs: [...(prev.related_acrs ?? []), { url: "", type: "primary" }],
    }));
  }, [setEvaluation]);

  const handleRelatedDelete = useCallback(
    (index) => {
      // eslint-disable-next-line no-alert -- intentional confirmation before delete
      if (!window.confirm("Are you sure you would like to delete this related ACR?")) return;
      setEvaluation((prev) => {
        const list = [...(prev.related_acrs ?? [])];
        list.splice(index, 1);
        const currentErrors = prev.about_field_format_errors ?? {};
        const nextErrors = Object.keys(currentErrors).reduce((acc, key) => {
          const match = key.match(/^related_acrs\[(\d+)\]\.url$/);
          if (!match) {
            acc[key] = currentErrors[key];
            return acc;
          }
          const oldIndex = Number(match[1]);
          if (oldIndex === index) return acc;
          const remappedKey = oldIndex > index
            ? `related_acrs[${oldIndex - 1}].url`
            : key;
          acc[remappedKey] = currentErrors[key];
          return acc;
        }, {});
        return {
          ...prev,
          related_acrs: list,
          about_field_format_errors: nextErrors,
        };
      });
    },
    [setEvaluation]
  );

  const handleRelatedChange = useCallback(
    (index, field, value) => {
      setEvaluation((prev) => {
        const list = [...(prev.related_acrs ?? [])];
        list[index] = { ...list[index], [field]: value };
        return { ...prev, related_acrs: list };
      });
    },
    [setEvaluation]
  );

  const updateChapterDisabled = useCallback(
    (chapterId, disabled) => {
      setEvaluation((prev) => {
        const nextChapters = { ...prev.chapters };
        nextChapters[chapterId] = { ...nextChapters[chapterId], disabled };
        return { ...prev, chapters: nextChapters };
      });
    },
    [setEvaluation]
  );

  const relatedAcrs = evaluation?.related_acrs ?? [];
  const catalogChapters = catalog?.chapters ?? [];
  const showCatalogSwitch = currentCatalogId != null && selectedCatalogId !== currentCatalogId;
  const catalogSwitchDisabled = currentCatalogId === selectedCatalogId;
  const inputClass = "usa-input";
  const textareaClass = "usa-textarea";
  const reportDateInputValue = normalizeDateToIso(evaluation?.report_date);
  const aboutFieldFormatErrors = evaluation?.about_field_format_errors ?? {};

  const updateAboutFieldFormatError = useCallback(
    (path, value) => {
      const nextMessage = getAboutFieldFormatError(path, value);
      setEvaluation((prev) => {
        const existing = prev.about_field_format_errors ?? {};
        const currentMessage = existing[path] ?? "";
        if ((currentMessage || "") === (nextMessage || "")) return prev;
        const nextErrors = { ...existing };
        if (nextMessage) {
          nextErrors[path] = nextMessage;
        } else {
          delete nextErrors[path];
        }
        return {
          ...prev,
          about_field_format_errors: nextErrors,
        };
      });
    },
    [setEvaluation]
  );
  const getValidatedFieldHandlers = useCallback(
    (validationPath, valuePath = validationPath) => ({
      onChange: (e) => {
        const nextValue = e.target.value;
        updateField(valuePath, nextValue);
        if (aboutFieldFormatErrors[validationPath]) {
          updateAboutFieldFormatError(validationPath, nextValue);
        }
      },
      onBlur: (e) => updateAboutFieldFormatError(validationPath, e.target.value),
    }),
    [aboutFieldFormatErrors, updateAboutFieldFormatError, updateField]
  );
  const authorEmailHandlers = getValidatedFieldHandlers("author.email");
  const authorPhoneHandlers = getValidatedFieldHandlers("author.phone");
  const authorWebsiteHandlers = getValidatedFieldHandlers("author.website");
  const vendorEmailHandlers = getValidatedFieldHandlers("vendor.email");
  const vendorPhoneHandlers = getValidatedFieldHandlers("vendor.phone");
  const vendorWebsiteHandlers = getValidatedFieldHandlers("vendor.website");
  const reportDateHandlers = getValidatedFieldHandlers("report_date");
  const repositoryHandlers = getValidatedFieldHandlers("repository");
  const feedbackHandlers = getValidatedFieldHandlers("feedback");

  return (
    <div className="about-page space-y-6">
      <Header>About</Header>
      <ChapterHelpText chapterId="about" />

      {/* Crash-recovery restore offer for on-device work (GSAACR-356). Two flavors: a brand-new
          draft with no server copy yet, or a reopened report whose on-device copy has edits the
          server never received (e.g. after leaving via the app nav before a save landed). */}
      {pendingRestore && (() => {
        const isReopenedReport = String(pendingRestore.scopeId ?? "").startsWith("report:");
        const savedAtSuffix = pendingRestore.savedAt
          ? ` from ${formatRelativeTimeFromNow(pendingRestore.savedAt)}`
          : "";
        return (
          <div className="mb-4" role="status" aria-live="polite">
            <Alert
              type="info"
              heading={
                isReopenedReport
                  ? "Newer changes for this ACR found on this device"
                  : "Unsaved ACR draft found on this device"
              }
            >
              <p className="usa-alert__text">
                {isReopenedReport
                  ? `This device has changes to this ACR${savedAtSuffix} that aren't in the saved version. Restore this device's changes or keep the saved version?`
                  : `You have an unsaved ACR draft on this device${savedAtSuffix}. Would you like to restore it or start fresh?`}
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                <Button type="button" onClick={restoreDraft}>
                  {isReopenedReport ? "Restore this device's changes" : "Restore draft"}
                </Button>
                <Button type="button" outline onClick={discardDraft}>
                  {isReopenedReport ? "Keep saved version" : "Start fresh"}
                </Button>
              </div>
            </Alert>
          </div>
        );
      })()}

      {/* Same expand/collapse control as Chapter page; toggles all <details> on this page */}
      <ExpandCollapseAll />

      {/* Select report type and catalog */}
      <details open className="criteria-details">
        <summary>
          <Link
            to="#select-catalog-editor"
            className="criteria-anchor-link"
            aria-label="Anchor link"
            onClick={(e) => e.stopPropagation()}
          >
            {anchorSvg}
          </Link>
          <span className="criteria-summary-inner">
            <h2 id="select-catalog-editor" className="criteria-title">
              Select Catalog
            </h2>
          </span>
        </summary>
        <p className="mb-2">{helpText.catalog?.intro}</p>
        {loadingList && <p className="text-gray-600">Loading catalogs…</p>}
        {listError && <p className="text-red-600">{listError}</p>}
        {!loadingList &&
          catalogList.length > 0 &&
          catalogList.map((c) => {
            const id = c.id ?? c.catalog;
            const title = formatCatalogLabel(c) || id;
            const catalogHelp = helpText.catalog?.[id];
            return (
              <div key={id} className="field">
                <label htmlFor={`evaluation-catalog-${id}`} className="flex items-center gap-2">
                  <input
                    id={`evaluation-catalog-${id}`}
                    type="radio"
                    name="catalog"
                    value={id}
                    checked={selectedCatalogId === id}
                    onChange={() => handleCatalogRadioChange(id)}
                    disabled={loadingCatalog}
                  />
                  {title}
                </label>
                {catalogHelp && <HelpText type="catalog" field={id} />}
              </div>
            );
          })}
        {!loadingList && catalogList.length === 0 && !listError && (
          <p className="text-gray-600">No catalogs available.</p>
        )}
        {loadingCatalog && <p className="text-gray-600 text-sm mt-1">Loading catalog…</p>}
        {showCatalogSwitch && (
          <p className="text-amber-700 text-sm mt-1 italic">
            Select Switch Catalogs to save your new selection.
          </p>
        )}
        <div className="flex gap-2 mt-2">
          <Button
            onClick={handleSwitchCatalog}
            disabled={catalogSwitchDisabled}
          >
            Switch Catalogs
          </Button>
          <Button
            onClick={handleResetCatalog}
            disabled={catalogSwitchDisabled}
            outline
          >
            Reset
          </Button>
        </div>
      </details>

      {/* Product */}
      <details open className="criteria-details">
        <summary>
          <Link
            to="#product-editor"
            className="criteria-anchor-link"
            aria-label="Anchor link"
            onClick={(e) => e.stopPropagation()}
          >
            {anchorSvg}
          </Link>
          <span className="criteria-summary-inner">
            <h2 id="product-editor" className="criteria-title">
              Product
            </h2>
          </span>
        </summary>
        {productSubmitError && (
          <div className="mb-4" role="alert" aria-live="assertive">
            <Alert
              type="error"
              heading="Submit failed"
              onClose={() => {
                setEvaluation((prev) => ({
                  ...prev,
                  product_submit_error: undefined,
                }));
              }}
            >
              {productSubmitError}
            </Alert>
          </div>
        )}
        {!isPublicMode && isSearchProductMode && (
          <Field
            id="evaluation-product-name"
            label="Search for an Existing Product:"
            error={productFieldError("name")}
          >
            {vendorId ? (
              <div className="product-name-search-wrapper relative">
                <TypeaheadCombobox
                  id="evaluation-product-name"
                  listboxId="product-suggestions-list"
                  value={productSearchInput}
                  onChange={setProductSearchInput}
                  options={productSuggestions}
                  getOptionKey={(suggestion) => suggestion.id}
                  getOptionDomId={(_, index) => `product-suggestion-${index}`}
                  renderOption={(suggestion) => (
                    <>
                      {suggestion.name}
                      {suggestion.isPublished === false ? " (Draft)" : ""}
                    </>
                  )}
                  onSelect={handleProductSuggestionSelect}
                  extraOptions={productSearchExtraOptions}
                  loading={productSearchLoading}
                  expanded={productDropdownOpen}
                  onExpandedChange={setProductDropdownOpen}
                  onInputFocusChange={(focused) => {
                    isProductSearchInputFocusedRef.current = focused;
                  }}
                  placeholder="Type to search for a product by name"
                  inputClassName={inputClass}
                  listClassName="product-name-suggestions"
                  optionClassName="product-name-suggestion"
                  highlightedClassName="product-name-suggestion-highlighted"
                  loadingClassName="product-name-suggestions-loading"
                />
                <p className="text-gray-600 text-sm mt-3 mb-2">
                  Or you can{" "}
                  <button
                    type="button"
                    onClick={handleAddNewProduct}
                    className="text-accent-cool underline bg-transparent border-0 p-0 cursor-pointer"
                  >
                    Add a New Product
                  </button>
                </p>
              </div>
            ) : (
              <input
                id="evaluation-product-name"
                type="text"
                value={productSearchInput}
                onChange={(e) => setProductSearchInput(e.target.value)}
                className={inputClass}
                placeholder="Enter product name"
              />
            )}
          </Field>
        )}
        {isPublicMode && (
          <>
            <ProductInformationSection
              product={product}
              categories={categories}
              pscCodes={pscCodes}
              fedRampStatuses={fedRampStatuses}
              clearFieldErrors={clearFieldErrors}
              setProduct={setProduct}
              getFieldError={getFieldError}
              renderInlineFieldError={renderInlineFieldError}
              FormFieldRequired={FormFieldRequired}
              fieldContainerClassName="!col-span-12 pr-5"
              fieldIds={{
                name: "evaluation-product-name",
                version: "evaluation-product-version",
                category: "evaluation-product-category",
                psc: "evaluation-product-psc",
                description: "evaluation-product-description",
                fedRamp: "evaluation-product-fedramp",
              }}
            />
            <ProductPocSection
              productPoc={product.productPoc ?? createEmptyProductPoc()}
              setProductPoc={setProductPoc}
              clearFieldErrors={clearFieldErrors}
              getFieldError={getFieldError}
              renderInlineFieldError={renderInlineFieldError}
              FormFieldRequired={FormFieldRequired}
              stateLabelValues={stateLabelValues}
            />
          </>
        )}
        {!isPublicMode && isSelectedExistingProductMode ? (
          <>
            {productSuccessBannerMessage ? (
              <div className="mb-4" role="status" aria-live="polite">
                <Alert
                  heading="Success"
                  type="success"
                  onClose={closeProductSuccessBanner}
                >
                  {productSuccessBannerMessage}
                </Alert>
              </div>
            ) : null}
            <div
              className="usa-alert usa-alert--info usa-alert--slim usa-alert--no-icon margin-bottom-2 product-status-alert"
              role="region"
              aria-label="Product status guidance"
            >
              <div className="usa-alert__body">
                <p className="usa-alert__text">
                  {product?.isPublished === false ? (
                    <>
                      This product is a draft. To submit or edit this product, go to{" "}
                      {selectedProductDetailsUrl ? (
                        <Link to={selectedProductDetailsUrl} className="text-accent-cool underline">
                          Product Details
                        </Link>
                      ) : (
                        "Product Details"
                      )}
                      .
                    </>
                  ) : (
                    <>
                      This product has been submitted. To edit this product, go to{" "}
                      {selectedProductDetailsUrl ? (
                        <Link to={selectedProductDetailsUrl} className="text-accent-cool underline">
                          Product Details
                        </Link>
                      ) : (
                        "Product Details"
                      )}
                      .
                    </>
                  )}
                </p>
              </div>
            </div>
            <ProductInformationSection
              product={product}
              categories={categories}
              pscCodes={pscCodes}
              fedRampStatuses={fedRampStatuses}
              clearFieldErrors={clearFieldErrors}
              setProduct={setProduct}
              getFieldError={getFieldError}
              renderInlineFieldError={renderInlineFieldError}
              FormFieldRequired={FormFieldRequired}
              showSectionHeader={false}
              readOnly
              fieldContainerClassName="!col-span-12 pr-5"
              fieldIds={{
                name: "evaluation-selected-product-name",
                version: "evaluation-selected-product-version",
                category: "evaluation-selected-product-category",
                psc: "evaluation-selected-product-psc",
                description: "evaluation-selected-product-description",
                fedRamp: "evaluation-selected-product-fedramp",
              }}
            />
            <ProductVisibilitySection
              normalizedProductVisibility={normalizedProductVisibility}
              clearFieldErrors={clearFieldErrors}
              setProduct={setProduct}
              getFieldError={getFieldError}
              renderInlineFieldError={renderInlineFieldError}
              FormFieldRequired={FormFieldRequired}
              agencyExclusionsApply={agencyExclusionsApply}
              selectedHiddenAgencies={selectedHiddenAgencies}
              product={product}
              agencies={agencies}
              hiddenAgencySelectionAnnouncement={hiddenAgencySelectionAnnouncement}
              agencyLoadError={agencyLoadError}
              sectionClassName="col-span-12 !mb-5"
              fieldContainerClassName="!col-span-12 pr-5"
              showSectionHeader={false}
              readOnly
              fieldIds={{
                visibility: "evaluation-selected-product-visibility",
                hiddenAgencyIds: "evaluation-selected-hidden-agency-ids",
              }}
            />
            <div className="mt-2">
              <button
                type="button"
                onClick={handleChooseDifferentProduct}
                className="expand-collapse-btn expand-collapse-btn-filled"
              >
                Choose a different product
              </button>
            </div>
          </>
        ) : null}
        {!isPublicMode && isUnknownProductMode && (
          <>
            <div className="mb-4" role="region" aria-label="Unknown product warning">
              <Alert type="warning" heading="Product not in your organization">
                {UNKNOWN_PRODUCT_WARNING}
              </Alert>
            </div>
            <Field id="evaluation-unknown-product-name" label="Product Name">
              <input
                id="evaluation-unknown-product-name"
                type="text"
                value={product?.name ?? ""}
                onChange={(e) => setProduct((prev) => ({ ...prev, name: e.target.value }))}
                className={inputClass}
              />
            </Field>
            <Field id="evaluation-unknown-product-version" label="Version">
              <input
                id="evaluation-unknown-product-version"
                type="text"
                value={product?.version ?? ""}
                onChange={(e) => setProduct((prev) => ({ ...prev, version: e.target.value }))}
                className={inputClass}
              />
            </Field>
            <Field id="evaluation-unknown-product-description" label="Description">
              <textarea
                id="evaluation-unknown-product-description"
                value={product?.description ?? ""}
                onChange={(e) => setProduct((prev) => ({ ...prev, description: e.target.value }))}
                rows={3}
                className={textareaClass}
              />
            </Field>
            <div className="mt-2">
              <button
                type="button"
                onClick={handleChooseDifferentProduct}
                className="expand-collapse-btn expand-collapse-btn-filled"
              >
                Choose a different product
              </button>
            </div>
          </>
        )}
        {!isPublicMode && isAddNewProductMode && (
          <>
            {productSuccessBannerMessage ? (
              <div className="mb-4" role="status" aria-live="polite">
                <Alert
                  heading="Success"
                  type="success"
                  onClose={closeProductSuccessBanner}
                >
                  {productSuccessBannerMessage}
                </Alert>
              </div>
            ) : null}
            <ProductInformationSection
              product={product}
              categories={categories}
              pscCodes={pscCodes}
              fedRampStatuses={fedRampStatuses}
              clearFieldErrors={clearFieldErrors}
              setProduct={setProduct}
              getFieldError={getFieldError}
              renderInlineFieldError={renderInlineFieldError}
              FormFieldRequired={FormFieldRequired}
              fieldContainerClassName="!col-span-12 pr-5"
              fieldIds={{
                name: "evaluation-product-name",
                version: "evaluation-product-version",
                category: "evaluation-product-category",
                psc: "evaluation-product-psc",
                description: "evaluation-product-description",
                fedRamp: "evaluation-product-fedramp",
              }}
              onNameBlur={handleProductNameBlur}
            />
            <ProductVisibilitySection
              normalizedProductVisibility={normalizedProductVisibility}
              clearFieldErrors={clearFieldErrors}
              setProduct={setProduct}
              getFieldError={getFieldError}
              renderInlineFieldError={renderInlineFieldError}
              FormFieldRequired={FormFieldRequired}
              agencyExclusionsApply={agencyExclusionsApply}
              selectedHiddenAgencies={selectedHiddenAgencies}
              product={product}
              agencies={agencies}
              hiddenAgencySelectionAnnouncement={hiddenAgencySelectionAnnouncement}
              agencyLoadError={agencyLoadError}
              sectionClassName="col-span-12 !mb-5"
              fieldContainerClassName="!col-span-12 pr-5"
              fieldIds={{
                visibility: "evaluation-product-visibility",
                hiddenAgencyIds: "evaluation-hidden-agency-ids",
              }}
            />
            <ProductPocSection
              productPoc={product.productPoc ?? createEmptyProductPoc()}
              setProductPoc={setProductPoc}
              clearFieldErrors={clearFieldErrors}
              getFieldError={getFieldError}
              renderInlineFieldError={renderInlineFieldError}
              FormFieldRequired={FormFieldRequired}
              stateLabelValues={stateLabelValues}
            />
            {showFinishProductReminder && (
              <div className="mb-4" role="status" aria-live="polite">
                <Alert type="info" heading="Finish your new product to continue">
                  Save it as a draft, submit it, or cancel it below. Autosave and submitting turn on
                  once your product is saved.
                </Alert>
              </div>
            )}
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleCancelNewProduct}
                className="expand-collapse-btn expand-collapse-btn-filled"
              >
                Cancel New Product
              </button>
              <button
                type="button"
                onClick={handleSaveDraftProduct}
                disabled={isProductActionInFlight}
                className="expand-collapse-btn expand-collapse-btn-filled"
              >
                Save Draft Product
              </button>
              <button
                type="button"
                onClick={handleSubmitNewProduct}
                disabled={isProductActionInFlight}
                className="expand-collapse-btn expand-collapse-btn-filled"
              >
                Submit New Product
              </button>
            </div>
          </>
        )}
      </details>

      {/* Author */}
      <details open className="criteria-details">
        <summary>
          <Link
            to="#author-editor"
            className="criteria-anchor-link"
            aria-label="Anchor link"
            onClick={(e) => e.stopPropagation()}
          >
            {anchorSvg}
          </Link>
          <span className="criteria-summary-inner">
            <h2 id="author-editor" className="criteria-title">
              Author
            </h2>
          </span>
        </summary>
        <Field id="evaluation-author-name" label="Name" helpType="author" helpField="name">
          <input
            id="evaluation-author-name"
            type="text"
            value={evaluation?.author?.name ?? ""}
            onChange={(e) => updateField("author.name", e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field
          id="evaluation-author-company"
          label="Company Name"
          helpType="author"
          helpField="company_name"
        >
          <input
            id="evaluation-author-company"
            type="text"
            value={evaluation?.author?.company_name ?? ""}
            onChange={(e) => updateField("author.company_name", e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field id="evaluation-author-address" label="Address" helpType="author" helpField="address">
          <input
            id="evaluation-author-address"
            type="text"
            value={evaluation?.author?.address ?? ""}
            onChange={(e) => updateField("author.address", e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field
          id="evaluation-author-email"
          label="Email"
          helpType="author"
          helpField="email"
          error={aboutFieldFormatErrors["author.email"]}
        >
          <input
            id="evaluation-author-email"
            type="email"
            value={evaluation?.author?.email ?? ""}
            onChange={authorEmailHandlers.onChange}
            onBlur={authorEmailHandlers.onBlur}
            className={inputClass}
          />
        </Field>
        <Field
          id="evaluation-author-phone"
          label="Phone"
          helpType="author"
          helpField="phone"
          error={aboutFieldFormatErrors["author.phone"]}
        >
          <input
            id="evaluation-author-phone"
            type="tel"
            value={evaluation?.author?.phone ?? ""}
            onChange={authorPhoneHandlers.onChange}
            onBlur={authorPhoneHandlers.onBlur}
            className={inputClass}
          />
        </Field>
        <Field
          id="evaluation-author-website"
          label="Website (URL)"
          helpType="author"
          helpField="website"
          error={aboutFieldFormatErrors["author.website"]}
        >
          <input
            id="evaluation-author-website"
            type="url"
            value={evaluation?.author?.website ?? ""}
            onChange={authorWebsiteHandlers.onChange}
            onBlur={authorWebsiteHandlers.onBlur}
            className={inputClass}
          />
        </Field>
      </details>

      {/* Vendor */}
      <details open className="criteria-details">
        <summary>
          <Link
            to="#vendor-editor"
            className="criteria-anchor-link"
            aria-label="Anchor link"
            onClick={(e) => e.stopPropagation()}
          >
            {anchorSvg}
          </Link>
          <span className="criteria-summary-inner">
            <h2 id="vendor-editor" className="criteria-title">
              Vendor
            </h2>
          </span>
        </summary>
        <Field id="evaluation-vendor-name" label="Name" helpType="vendor" helpField="name">
          <input
            id="evaluation-vendor-name"
            type="text"
            value={evaluation?.vendor?.name ?? ""}
            onChange={(e) => updateField("vendor.name", e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field
          id="evaluation-vendor-company"
          label="Company Name"
          helpType="vendor"
          helpField="company_name"
        >
          <input
            id="evaluation-vendor-company"
            type="text"
            value={evaluation?.vendor?.company_name ?? ""}
            onChange={(e) => updateField("vendor.company_name", e.target.value)}
            className={inputClass}
            disabled
            readOnly
          />
        </Field>
        <Field id="evaluation-vendor-address" label="Address" helpType="vendor" helpField="address">
          <input
            id="evaluation-vendor-address"
            type="text"
            value={evaluation?.vendor?.address ?? ""}
            onChange={(e) => updateField("vendor.address", e.target.value)}
            className={inputClass}
            disabled
            readOnly
          />
        </Field>
        <Field
          id="evaluation-vendor-email"
          label="Email"
          helpType="vendor"
          helpField="email"
          error={aboutFieldFormatErrors["vendor.email"]}
        >
          <input
            id="evaluation-vendor-email"
            type="email"
            value={evaluation?.vendor?.email ?? ""}
            onChange={vendorEmailHandlers.onChange}
            onBlur={vendorEmailHandlers.onBlur}
            className={inputClass}
          />
        </Field>
        <Field
          id="evaluation-vendor-phone"
          label="Phone"
          helpType="vendor"
          helpField="phone"
          error={aboutFieldFormatErrors["vendor.phone"]}
        >
          <input
            id="evaluation-vendor-phone"
            type="tel"
            value={evaluation?.vendor?.phone ?? ""}
            onChange={vendorPhoneHandlers.onChange}
            onBlur={vendorPhoneHandlers.onBlur}
            className={inputClass}
          />
        </Field>
        <Field
          id="evaluation-vendor-website"
          label="Website (URL)"
          helpType="vendor"
          helpField="website"
          error={aboutFieldFormatErrors["vendor.website"]}
        >
          <input
            id="evaluation-vendor-website"
            type="url"
            value={evaluation?.vendor?.website ?? ""}
            onChange={vendorWebsiteHandlers.onChange}
            onBlur={vendorWebsiteHandlers.onBlur}
            className={inputClass}
          />
        </Field>
      </details>

      {/* ACR Report Details */}
      <details open className="criteria-details">
        <summary>
          <Link
            to="#acr-report-details-editor"
            className="criteria-anchor-link"
            aria-label="Anchor link"
            onClick={(e) => e.stopPropagation()}
          >
            {anchorSvg}
          </Link>
          <span className="criteria-summary-inner">
            <h2 id="acr-report-details-editor" className="criteria-title">
              ACR Report Details
            </h2>
          </span>
        </summary>
        <Field
          id="evaluation-report-date"
          label="Report Date"
          helpType="report"
          helpField="report_date"
          error={aboutFieldFormatErrors.report_date}
        >
          <input
            id="evaluation-report-date"
            type="date"
            value={reportDateInputValue}
            onChange={reportDateHandlers.onChange}
            onBlur={reportDateHandlers.onBlur}
            className={inputClass}
          />
        </Field>
        <Field
          id="evaluation-last-modified-date"
          label="Last Modified Date"
          helpType="report"
          helpField="last_modified_date"
        >
          <input
            id="evaluation-last-modified-date"
            type="text"
            value={evaluation?.last_modified_date ?? ""}
            readOnly
            className={inputClass}
          />
        </Field>
        <div className="field mb-4">
          <label className="usa-label" htmlFor="evaluation-version">
            <span className="block mb-1">Version</span>
            <span className="version-prefix">{versionPrefix}-</span>
            <input
              type="text"
              readOnly
              value={evaluation?.version ?? "1"}
              id="evaluation-version"
              className={inputClass}
            />
          </label>
          <HelpText type="report" field="version" />
        </div>
        <Field id="evaluation-notes" label="Notes" helpType="report" helpField="notes">
          <textarea
            id="evaluation-notes"
            value={evaluation?.notes ?? ""}
            onChange={(e) => updateField("notes", e.target.value)}
            rows={3}
            className={textareaClass}
          />
        </Field>
        <Field
          id="evaluation-evaluation-methods-used"
          label="Evaluation Methods Used"
          helpType="report"
          helpField="evaluation_methods_used"
        >
          <textarea
            id="evaluation-evaluation-methods-used"
            value={evaluation?.evaluation_methods_used ?? ""}
            onChange={(e) => updateField("evaluation_methods_used", e.target.value)}
            rows={3}
            className={textareaClass}
          />
        </Field>
        <Field
          id="evaluation-legal-disclaimer"
          label="Legal Disclaimer"
          helpType="report"
          helpField="legal_disclaimer"
        >
          <textarea
            id="evaluation-legal-disclaimer"
            value={evaluation?.legal_disclaimer ?? ""}
            onChange={(e) => updateField("legal_disclaimer", e.target.value)}
            rows={2}
            className={textareaClass}
          />
        </Field>
        <Field
          id="evaluation-repository"
          label="Repository (URL)"
          helpType="report"
          helpField="repository"
          error={aboutFieldFormatErrors.repository}
        >
          <input
            id="evaluation-repository"
            type="url"
            value={evaluation?.repository ?? ""}
            onChange={repositoryHandlers.onChange}
            onBlur={repositoryHandlers.onBlur}
            className={inputClass}
          />
        </Field>
        <Field
          id="evaluation-feedback"
          label="Feedback (URL)"
          helpType="report"
          helpField="feedback"
          error={aboutFieldFormatErrors.feedback}
        >
          <input
            id="evaluation-feedback"
            type="url"
            value={evaluation?.feedback ?? ""}
            onChange={feedbackHandlers.onChange}
            onBlur={feedbackHandlers.onBlur}
            className={inputClass}
          />
        </Field>
        <Field id="evaluation-license" label="License" helpType="report" helpField="license">
          <LicenseSelect
            id="evaluation-license"
            options={LICENSE_OPTIONS}
            value={evaluation?.license ?? ""}
            onChange={(val) => updateField("license", val)}
            placeholder="Select license…"
          />
        </Field>
      </details>

      {/* Related ACR Reports */}
      <details open className="criteria-details">
        <summary>
          <Link
            to="#related-acrs-editor"
            className="criteria-anchor-link"
            aria-label="Anchor link"
            onClick={(e) => e.stopPropagation()}
          >
            {anchorSvg}
          </Link>
          <span className="criteria-summary-inner">
            <h2 id="related-acrs-editor" className="criteria-title">
              Related ACR Reports
            </h2>
          </span>
        </summary>
        <p className="mb-2">{helpText.related_acrs?.intro}</p>
        {relatedAcrs.map((related, index) => (
          <Related
            key={index} // eslint-disable-line react/no-array-index-key -- items have no stable id; index needed for callbacks
            index={index}
            url={related.url ?? ""}
            urlError={aboutFieldFormatErrors[`related_acrs[${index}].url`]}
            type={related.type === "secondary" ? "secondary" : "primary"}
            onUrlChange={(value) => {
              handleRelatedChange(index, "url", value);
              const path = `related_acrs[${index}].url`;
              if (aboutFieldFormatErrors[path]) {
                updateAboutFieldFormatError(path, value);
              }
            }}
            onUrlBlur={(e) => updateAboutFieldFormatError(`related_acrs[${index}].url`, e.target.value)}
            onTypeChange={(value) => handleRelatedChange(index, "type", value)}
            onDelete={() => handleRelatedDelete(index)}
          />
        ))}
        <Button onClick={handleRelatedAdd}>
          Add related ACR report
        </Button>
      </details>

      {/* Disabled Chapters/Tables */}
      {catalogChapters.length > 0 && (
        <details open className="criteria-details">
          <summary>
            <Link
              to="#disabled-chapters-editor"
              className="criteria-anchor-link"
              aria-label="Anchor link"
              onClick={(e) => e.stopPropagation()}
            >
              {anchorSvg}
            </Link>
            <span className="criteria-summary-inner">
              <h2 id="disabled-chapters-editor" className="criteria-title">
                Disabled Chapters/Tables
              </h2>
            </span>
          </summary>
          <p className="mb-2">{helpText.disabled_chapters?.intro}</p>
          {catalogChapters.map((chapter) => {
            const chapterId = chapter.id;
            const chapterData = evaluation?.chapters?.[chapterId];
            const disabled = Boolean(chapterData?.disabled);
            const label = chapter.label ?? chapter.short_label ?? chapterId;
            const isWebChapter = chapterId === "web";
            return (
              <div key={chapterId} className="field">
                <label
                  htmlFor={`evaluation-disabled-chapter-${chapterId}`}
                  className="flex items-center gap-2"
                >
                  <input
                    id={`evaluation-disabled-chapter-${chapterId}`}
                    type="checkbox"
                    checked={disabled}
                    onChange={(e) => updateChapterDisabled(chapterId, e.target.checked)}
                    disabled={isWebChapter}
                  />
                  {label}
                </label>
                <HelpText type="disabled_chapters" field={chapterId} />
              </div>
            );
          })}
        </details>
      )}

      <Pager label="Previous/Next">
        <EditorPagerLink to={ACR_EDITOR_OVERVIEW_URL} direction="previous">
          Overview
        </EditorPagerLink>
        {catalogChapters.length > 0 && (
          <EditorPagerLink
            to={`${ACR_EDITOR_CHAPTER_URL}/${catalogChapters[0].id}`}
            direction="next"
          >
            {catalogChapters[0].short_label ?? catalogChapters[0].label ?? catalogChapters[0].id}
          </EditorPagerLink>
        )}
      </Pager>
    </div>
  );
}
